import {Box, ThemeContainer, ThemeProvider} from '@dropbox/dig-foundations';
import {
  DeleteLine,
  HighlightLine,
  TableHeaderColumnLine,
  TableHeaderRowLine,
} from '@dropbox/dig-icons/assets';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {
  $computeTableMapSkipCellCheck,
  $deleteTableColumnAtSelection,
  $deleteTableRowAtSelection,
  $getTableNodeFromLexicalNodeOrThrow,
  $isTableCellNode,
  TableCellHeaderStates,
  TableCellNode,
  TableSelection,
} from '@lexical/table';
import {pulseUserAtom} from 'atoms/auth';
import {derivedThemeAtom} from 'atoms/layout';
import {canManagePlanningTemplates} from 'helpers/utils';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {$createParagraphNode} from 'lexical';
import {useIsTemplateMode} from 'plugins/enhancedHeadings';
import {useCallback, useEffect, useRef, useState} from 'react';

import {ColorSelectionToolbar} from './ColorSelectionToolbar';
import {ToolbarButton} from './ToolbarButton';

export type TableSelectionInfo = {
  isFullRowSelection: boolean;
  isFullColumnSelection: boolean;
  selectedRows: number[];
  selectedColumns: number[];
  tableRows: number;
  tableColumns: number;
  enableAddRows: boolean;
  enableAddColumns: boolean;
};

type TableToolbarProps = {
  tableSelection: TableSelection | null;
  tableSelectionInfo: TableSelectionInfo;
};

export const TableToolbar = ({tableSelection, tableSelectionInfo}: TableToolbarProps) => {
  const [editor] = useLexicalComposerContext();
  const isDarkMode = useAtomValue(derivedThemeAtom) === 'dark';
  const toolbarRef = useRef<HTMLDivElement>(null);
  const isTemplateMode = useIsTemplateMode();
  const pulseUser = useAtomValue(pulseUserAtom);
  const canManageTemplates = canManagePlanningTemplates(pulseUser);

  // State for header status
  const [isRowHeader, setIsRowHeader] = useState(false);
  const [isColumnHeader, setIsColumnHeader] = useState(false);

  // State for color selection toolbar
  const [showColorToolbar, setShowColorToolbar] = useState(false);

  /**
   * Updates the header states by checking the selected rows/columns
   */
  useEffect(() => {
    if (!tableSelection) {
      setIsRowHeader(false);
      setIsColumnHeader(false);
      return;
    }

    editor.read(() => {
      try {
        const nodes = tableSelection.getNodes();
        const tableCells = nodes.filter($isTableCellNode);

        if (tableCells.length === 0) {
          setIsRowHeader(false);
          setIsColumnHeader(false);
          return;
        }

        const firstCell = tableCells[0];

        // Check if the node still exists in the editor state
        if (!firstCell.isAttached()) {
          setIsRowHeader(false);
          setIsColumnHeader(false);
          return;
        }

        let rowHeaderState = false;
        let columnHeaderState = false;

        const tableNode = $getTableNodeFromLexicalNodeOrThrow(firstCell);
        const [gridMap] = $computeTableMapSkipCellCheck(tableNode, null, null);

        // Check if any selected rows have row header styling (top row)
        if (tableSelectionInfo.isFullRowSelection) {
          const firstCellInRow = gridMap[0]?.[0]?.cell;
          rowHeaderState = !!(
            firstCellInRow &&
            (firstCellInRow.getHeaderStyles() & TableCellHeaderStates.ROW) ===
              TableCellHeaderStates.ROW
          );
        }

        // Check if any selected columns have column header styling (left column)
        if (tableSelectionInfo.isFullColumnSelection) {
          const firstCellInColumn = gridMap[0]?.[0]?.cell;
          columnHeaderState = !!(
            firstCellInColumn &&
            (firstCellInColumn.getHeaderStyles() & TableCellHeaderStates.COLUMN) ===
              TableCellHeaderStates.COLUMN
          );
        }

        setIsRowHeader(rowHeaderState);
        setIsColumnHeader(columnHeaderState);
      } catch (error) {
        console.error('Error checking header states:', error);
        setIsRowHeader(false);
        setIsColumnHeader(false);
      }
    });
  }, [editor, tableSelection, tableSelectionInfo]);

  const handleDeleteRows = useCallback(() => {
    if (!tableSelectionInfo.isFullRowSelection) return;

    editor.update(() => {
      try {
        $deleteTableRowAtSelection();
      } catch (error) {
        console.error('Error deleting rows:', error);
      }
    });
  }, [editor, tableSelectionInfo]);

  const handleDeleteColumns = useCallback(() => {
    if (!tableSelectionInfo.isFullColumnSelection) return;

    editor.update(() => {
      try {
        $deleteTableColumnAtSelection();
      } catch (error) {
        console.error('Error deleting columns:', error);
      }
    });
  }, [editor, tableSelectionInfo]);

  const handleDeleteTable = useCallback(() => {
    if (!tableSelection) return;

    editor.update(() => {
      try {
        const nodes = tableSelection.getNodes();
        const tableCells = nodes.filter($isTableCellNode);
        if (tableCells.length === 0) return;

        const firstCell = tableCells[0];

        // Check if the node still exists in the editor state
        if (!firstCell.isAttached()) return;

        const tableNode = $getTableNodeFromLexicalNodeOrThrow(firstCell);

        // Insert a paragraph node after the table to place the cursor
        const newParagraph = $createParagraphNode();
        tableNode.insertAfter(newParagraph);

        // Remove the table
        tableNode.remove();

        // Clear the selection and set cursor to the new paragraph
        newParagraph.select();
      } catch (error) {
        console.error('Error deleting table:', error);
      }
    });
  }, [editor, tableSelection]);

  const getLastHighlightColor = useCallback(() => {
    if (!tableSelection) return 'var(--dig-color__primary__surface)';

    let lastHighlightColor = null;

    editor.read(() => {
      const nodes = tableSelection.getNodes();
      const tableCells = nodes.filter($isTableCellNode);

      if (tableCells.length === 0) return;

      // Get table node and grid map for comprehensive checking
      const firstCell = tableCells[0];
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(firstCell);
      const [gridMap] = $computeTableMapSkipCellCheck(tableNode, null, null);

      // Check row selections for existing highlights
      if (tableSelectionInfo.isFullRowSelection) {
        const processedRows = new Set<string>();

        for (const cell of tableCells) {
          const rowNode = cell.getParent();
          if (!rowNode || processedRows.has(rowNode.getKey())) continue;

          processedRows.add(rowNode.getKey());

          const rowCells = rowNode.getChildren().filter($isTableCellNode);
          const hasHeaderCells = rowCells.some((c) => c.hasHeader());

          if (!hasHeaderCells) {
            // Check each cell in the row for existing highlights
            for (const rowCell of rowCells) {
              const backgroundColor = rowCell.getBackgroundColor();
              if (backgroundColor) {
                lastHighlightColor = backgroundColor;
              }
            }
          }
        }
      }

      // Check column selections for existing highlights
      if (tableSelectionInfo.isFullColumnSelection) {
        tableSelectionInfo.selectedColumns.forEach((columnIndex) => {
          // Iterate through all rows for this column
          for (let rowIndex = 0; rowIndex < gridMap.length; rowIndex++) {
            const cellInfo = gridMap[rowIndex]?.[columnIndex];
            if (cellInfo?.cell && !cellInfo.cell.hasHeader()) {
              const backgroundColor = cellInfo.cell.getBackgroundColor();
              if (backgroundColor) {
                lastHighlightColor = backgroundColor;
              }
            }
          }
        });
      }
    });

    return lastHighlightColor || 'var(--dig-color__primary__surface)';
  }, [editor, tableSelection, tableSelectionInfo]);

  const applyHighlightToSelection = useCallback(
    (color: string | null) => {
      if (!tableSelection) return;

      editor.update(() => {
        try {
          const nodes = tableSelection.getNodes();
          const tableCells = nodes.filter($isTableCellNode);

          if (tableCells.length === 0) return;

          // Get table node and grid map for column highlighting
          const firstCell = tableCells[0];
          const tableNode = $getTableNodeFromLexicalNodeOrThrow(firstCell);
          const [gridMap] = $computeTableMapSkipCellCheck(tableNode, null, null);

          // Handle row highlighting (existing logic)
          if (tableSelectionInfo.isFullRowSelection) {
            const processedRows = new Set<string>();

            for (const cell of tableCells) {
              const rowNode = cell.getParent();
              if (!rowNode || processedRows.has(rowNode.getKey())) continue;

              processedRows.add(rowNode.getKey());

              const rowCells = rowNode.getChildren().filter($isTableCellNode);
              const hasHeaderCells = rowCells.some((c) => c.hasHeader());

              if (!hasHeaderCells) {
                rowCells.forEach((c) => {
                  c.setBackgroundColor(color);
                });
              }
            }
          }

          // Handle column highlighting (new logic)
          if (tableSelectionInfo.isFullColumnSelection) {
            tableSelectionInfo.selectedColumns.forEach((columnIndex) => {
              // Iterate through all rows for this column
              for (let rowIndex = 0; rowIndex < gridMap.length; rowIndex++) {
                const cellInfo = gridMap[rowIndex]?.[columnIndex];
                if (cellInfo?.cell && !cellInfo.cell.hasHeader()) {
                  cellInfo.cell.setBackgroundColor(color);
                }
              }
            });
          }
        } catch (error) {
          console.error('Error highlighting cells:', error);
        }
      });
    },
    [editor, tableSelection, tableSelectionInfo]
  );

  const handleHighlightCells = useCallback(() => {
    // Show the color toolbar
    setShowColorToolbar(!showColorToolbar);

    // Apply the default color immediately
    const defaultColor = getLastHighlightColor();
    applyHighlightToSelection(defaultColor);
  }, [showColorToolbar, getLastHighlightColor, applyHighlightToSelection]);

  const applyHighlightColor = useCallback(
    (color: string) => {
      applyHighlightToSelection(color);
      // Close the color toolbar after applying color
      setShowColorToolbar(false);
    },
    [applyHighlightToSelection]
  );

  const resetHighlight = useCallback(() => {
    applyHighlightToSelection(null);
    // Close the color toolbar after resetting
    setShowColorToolbar(false);
  }, [applyHighlightToSelection]);

  const handleToggleRowHeader = useCallback(() => {
    if (!tableSelectionInfo.isFullRowSelection || !tableSelection) return;

    editor.update(() => {
      try {
        // Get nodes inside editor context to avoid "no active editor state" error
        const nodes = tableSelection.getNodes();
        const tableCells = nodes.filter($isTableCellNode);
        if (tableCells.length === 0) return;

        const firstCell = tableCells[0];
        if (!firstCell.isAttached()) return;

        const tableNode = $getTableNodeFromLexicalNodeOrThrow(firstCell);
        const [gridMap] = $computeTableMapSkipCellCheck(tableNode, null, null);
        const topRowCells = new Set<TableCellNode>();

        // Get the current header state from the top row
        const firstTopCell = gridMap[0]?.[0]?.cell;
        const newStyle = firstTopCell
          ? firstTopCell.getHeaderStyles() ^ TableCellHeaderStates.ROW
          : TableCellHeaderStates.ROW;

        // Apply header styling to the top row (row 0)
        for (let col = 0; col < gridMap[0].length; col++) {
          const mapCell = gridMap[0][col];
          if (!mapCell?.cell) continue;

          if (!topRowCells.has(mapCell.cell)) {
            topRowCells.add(mapCell.cell);
            mapCell.cell.setHeaderStyles(newStyle, TableCellHeaderStates.ROW);
          }
        }
      } catch (error) {
        console.error('Error toggling row headers:', error);
      }
    });
  }, [editor, tableSelection, tableSelectionInfo]);

  const handleToggleColumnHeader = useCallback(() => {
    if (!tableSelectionInfo.isFullColumnSelection || !tableSelection) return;

    editor.update(() => {
      try {
        // Get nodes inside editor context to avoid "no active editor state" error
        const nodes = tableSelection.getNodes();
        const tableCells = nodes.filter($isTableCellNode);
        if (tableCells.length === 0) return;

        const firstCell = tableCells[0];
        if (!firstCell.isAttached()) return;

        const tableNode = $getTableNodeFromLexicalNodeOrThrow(firstCell);
        const [gridMap] = $computeTableMapSkipCellCheck(tableNode, null, null);
        const leftColumnCells = new Set<TableCellNode>();

        // Get the current header state from the left column
        const firstLeftCell = gridMap[0]?.[0]?.cell;
        const newStyle = firstLeftCell
          ? firstLeftCell.getHeaderStyles() ^ TableCellHeaderStates.COLUMN
          : TableCellHeaderStates.COLUMN;

        // Apply header styling to the leftmost column (column 0)
        for (let row = 0; row < gridMap.length; row++) {
          const mapCell = gridMap[row][0];
          if (!mapCell?.cell) continue;

          if (!leftColumnCells.has(mapCell.cell)) {
            leftColumnCells.add(mapCell.cell);
            mapCell.cell.setHeaderStyles(newStyle, TableCellHeaderStates.COLUMN);
          }
        }
      } catch (error) {
        console.error('Error toggling column headers:', error);
      }
    });
  }, [editor, tableSelection, tableSelectionInfo]);

  return (
    <ThemeProvider mode={!isDarkMode ? 'dark' : 'bright'}>
      <ThemeContainer>
        <Box display="flex" flexDirection="column" alignItems="flex-start" style={{gap: 8}}>
          <Box
            ref={toolbarRef}
            boxShadow="Floating"
            backgroundColor="Background Base"
            borderRadius="Large"
            paddingY="4"
            paddingX="8"
            display="flex"
            flexDirection="row"
            style={{gap: 8}}
          >
            {/* Highlight Action */}
            {(tableSelectionInfo.isFullRowSelection || tableSelectionInfo.isFullColumnSelection) &&
              (canManageTemplates || isTemplateMode) && (
                <ToolbarButton
                  title={t('toolbar_highlight_cells')}
                  onClick={handleHighlightCells}
                  icon={HighlightLine}
                  active={showColorToolbar}
                />
              )}

            {/* Header Toggle Actions - only show when first row/column is selected */}
            {tableSelectionInfo.isFullRowSelection &&
              tableSelectionInfo.selectedRows.includes(0) &&
              (canManageTemplates || isTemplateMode) && (
                <ToolbarButton
                  title={isRowHeader ? t('remove_row_header') : t('add_row_header')}
                  active={isRowHeader}
                  onClick={handleToggleRowHeader}
                  icon={TableHeaderRowLine}
                />
              )}

            {tableSelectionInfo.isFullColumnSelection &&
              tableSelectionInfo.selectedColumns.includes(0) &&
              isTemplateMode && (
                <ToolbarButton
                  title={isColumnHeader ? t('remove_column_header') : t('add_column_header')}
                  active={isColumnHeader}
                  onClick={handleToggleColumnHeader}
                  icon={TableHeaderColumnLine}
                />
              )}

            {/* Delete Actions */}
            {tableSelectionInfo.isFullRowSelection &&
            tableSelectionInfo.isFullColumnSelection &&
            tableSelectionInfo.selectedRows.length === tableSelectionInfo.tableRows &&
            tableSelectionInfo.selectedColumns.length === tableSelectionInfo.tableColumns ? (
              <ToolbarButton
                title={t('toolbar_delete_table')}
                onClick={handleDeleteTable}
                icon={DeleteLine}
              />
            ) : (
              <>
                {tableSelectionInfo.isFullRowSelection && !isColumnHeader && (
                  <ToolbarButton
                    title={t('toolbar_delete_rows', {
                      count: tableSelectionInfo.selectedRows.length,
                    })}
                    onClick={handleDeleteRows}
                    icon={DeleteLine}
                  />
                )}

                {tableSelectionInfo.isFullColumnSelection && !isRowHeader && (
                  <ToolbarButton
                    title={t('toolbar_delete_columns', {
                      count: tableSelectionInfo.selectedColumns.length,
                    })}
                    onClick={handleDeleteColumns}
                    icon={DeleteLine}
                  />
                )}
              </>
            )}
          </Box>

          {/* Color Selection Toolbar */}
          {showColorToolbar && (
            <ColorSelectionToolbar
              onColorSelect={applyHighlightColor}
              onReset={resetHighlight}
              isDarkMode={isDarkMode}
            />
          )}
        </Box>
      </ThemeContainer>
    </ThemeProvider>
  );
};
